import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';

import { AuthConfig, AuthStep } from '@/shared/types/node';
import { capitalizeFirstLetter } from '@/util/util';
import { authQuestionSchema, authStepFailureSchema } from '@/util/validation/schemas/auth-schemas';

import { AuthStepKey } from './types';

export type FieldError = { field: string; message: string };

const useAuthPageValidation = (authConfig: AuthConfig, showErrors = false) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const prevErrorsRef = useRef<Map<string, string>>(new Map());

  const setFieldError = useCallback((field: string, message: string | undefined) => {
    setErrors((prev) => {
      const next = new Map(prev);
      if (message) {
        next.set(field, capitalizeFirstLetter(message));
      } else {
        next.delete(field);
      }
      prevErrorsRef.current = next;
      return next;
    });
  }, []);

  const validateStep = useCallback(
    (stepKey: AuthStepKey, stepConfig: AuthStep | undefined) => {
      const enabled = stepConfig?.enabled ?? false;
      const questions = stepConfig?.questions ?? [];

      // Clear errors if step not enabled
      if (!enabled) {
        for (const key of prevErrorsRef.current.keys()) {
          if (key.startsWith(`${stepKey}-`)) {
            setFieldError(key, undefined);
          }
        }
        return;
      }

      // Step enabled but no questions — backend rejects this
      if (questions.length === 0) {
        for (const key of prevErrorsRef.current.keys()) {
          if (key.startsWith(`${stepKey}-`) && key !== `${stepKey}-questions`) {
            setFieldError(key, undefined);
          }
        }
        setFieldError(`${stepKey}-questions`, t('dialog.auth.validation_questions_required'));
        return;
      }
      setFieldError(`${stepKey}-questions`, undefined);

      // Validate questions
      questions.forEach((question, idx) => {
        try {
          authQuestionSchema.validateSync(question, { abortEarly: false });
          setFieldError(`${stepKey}-q-${idx}-question`, undefined);
          setFieldError(`${stepKey}-q-${idx}-save_as`, undefined);
          setFieldError(`${stepKey}-q-${idx}-type`, undefined);
          setFieldError(`${stepKey}-q-${idx}-points`, undefined);
          setFieldError(`${stepKey}-q-${idx}-validation`, undefined);
        } catch (err) {
          if (err instanceof ValidationError) {
            const fieldErrors = new Map<string, string>();
            for (const inner of err.inner) {
              const path = inner.path ?? '';
              if (path === 'question' || path === '') {
                fieldErrors.set('question', inner.message);
              } else if (path === 'save_as') {
                fieldErrors.set('save_as', inner.message);
              } else if (path === 'type') {
                fieldErrors.set('type', inner.message);
              } else if (path === 'points') {
                fieldErrors.set('points', inner.message);
              } else if (path.startsWith('validation')) {
                fieldErrors.set('validation', inner.message);
              }
            }
            setFieldError(`${stepKey}-q-${idx}-question`, fieldErrors.get('question'));
            setFieldError(`${stepKey}-q-${idx}-save_as`, fieldErrors.get('save_as'));
            setFieldError(`${stepKey}-q-${idx}-type`, fieldErrors.get('type'));
            setFieldError(`${stepKey}-q-${idx}-points`, fieldErrors.get('points'));
            setFieldError(`${stepKey}-q-${idx}-validation`, fieldErrors.get('validation'));
          }
        }
      });

      // Clear stale errors for removed questions
      for (const key of prevErrorsRef.current.keys()) {
        if (!key.startsWith(`${stepKey}-q-`)) {
          continue;
        }
        const match = key.match(new RegExp(`^${stepKey}-q-(\\d+)-`));
        if (match && parseInt(match[1], 10) >= questions.length) {
          setFieldError(key, undefined);
        }
      }

      // Validate threshold
      const threshold = stepConfig?.success_points_threshold;
      if (threshold != null && threshold < 1) {
        setFieldError(`${stepKey}-threshold`, t('validation.min_value', { 0: 1 }));
      } else {
        setFieldError(`${stepKey}-threshold`, undefined);
      }

      // Validate failure config
      const failure = stepConfig?.failure;
      if (failure) {
        try {
          authStepFailureSchema.validateSync(failure, { abortEarly: false });
          setFieldError(`${stepKey}-failure-max_attempts`, undefined);
          setFieldError(`${stepKey}-failure-max_step_minutes`, undefined);
          setFieldError(`${stepKey}-failure-guideline`, undefined);
        } catch (err) {
          if (err instanceof ValidationError) {
            const fieldErrors = new Map<string, string>();
            for (const inner of err.inner) {
              if (inner.path) {
                fieldErrors.set(inner.path, inner.message);
              }
            }
            setFieldError(`${stepKey}-failure-max_attempts`, fieldErrors.get('max_attempts'));
            setFieldError(
              `${stepKey}-failure-max_step_minutes`,
              fieldErrors.get('max_step_minutes')
            );
            setFieldError(`${stepKey}-failure-guideline`, fieldErrors.get('guideline'));
          }
        }
      }
    },
    [setFieldError, t]
  );

  const validateNodeIds = useCallback(() => {
    const hasAnyEnabledStep =
      !!authConfig.identification_step?.enabled || !!authConfig.verification_step?.enabled;

    if (hasAnyEnabledStep) {
      setFieldError(
        'success_node_id',
        authConfig.success_node_id ? undefined : t('validation.required')
      );
      setFieldError(
        'failure_node_id',
        authConfig.failure_node_id ? undefined : t('validation.required')
      );
    } else {
      setFieldError('success_node_id', undefined);
      setFieldError('failure_node_id', undefined);
    }
  }, [authConfig, setFieldError, t]);

  useEffect(() => {
    validateStep('identification', authConfig.identification_step);
    validateStep('verification', authConfig.verification_step);
    validateNodeIds();
  }, [authConfig, validateStep, validateNodeIds]);

  const getFieldError = useCallback(
    (field: string) => (showErrors ? errors.get(field) : undefined),
    [errors, showErrors]
  );

  const hasErrors = errors.size > 0;

  const getQuestionIndicesWithErrors = useCallback(
    (stepKey: AuthStepKey) => {
      const indices = new Set<number>();
      if (!showErrors) {
        return indices;
      }
      for (const key of errors.keys()) {
        const match = key.match(new RegExp(`^${stepKey}-q-(\\d+)-`));
        if (match) {
          indices.add(parseInt(match[1], 10));
        }
      }
      return indices;
    },
    [errors, showErrors]
  );

  const getThresholdExceedsMax = useCallback(
    (stepKey: AuthStepKey) => {
      const stepFieldKey = `${stepKey}_step` as const;
      const stepConfig = authConfig[stepFieldKey];
      if (!stepConfig?.enabled) {
        return false;
      }
      const maxPoints = stepConfig.questions?.reduce((sum, q) => sum + (q.points || 0), 0) ?? 0;
      const threshold = stepConfig.success_points_threshold ?? 0;
      return threshold > 0 && maxPoints > 0 && threshold > maxPoints;
    },
    [authConfig]
  );

  return { errors, getFieldError, hasErrors, getQuestionIndicesWithErrors, getThresholdExceedsMax };
};

export default useAuthPageValidation;
